import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Stack, Text } from '@chakra-ui/react';
import {
    activeAlternative,
    choiceLabels,
    chooseAlternative,
    dependencyValues,
    fieldState,
    fieldsInPlay,
    readForm,
    setField,
    summarize,
    type Choice,
    type Form,
    type FormField,
    type FormPath,
    type FormValue,
    type SourceDescription,
} from '@casa/driver-sdk';
import type { SlotNeeds } from '@casa/model';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';
import { useAsk, Waiting, Failed } from '../ui/asking.tsx';
import { ChoiceField, ScreenRow, TextFieldRow, ToggleRow, WaitingRow } from './form_rows.tsx';

// A form from `@casa/driver-sdk`'s form language (dashboard-editor spec §5.6), as rows for a
// `SettingsGroup`: a choice, a text, a switch, a sub-form or a list that opens a screen of its own
// (to any depth), a sub-form the driver gives at run time, and, where the form has alternatives,
// the way first. A field that waits for others is greyed, saying for what; a hidden one is left
// out. What a change clears, and what shows, is the SDK's (`setField`, `fieldState`).

/** More choices than this get a filter. */
const FILTER_FROM = 12;

/** What the form asks whoever knows the answers: a driver, through its probe. */
export interface FormAsk {
    /** The values a listed field can take, given the whole form's value (`root`). */
    choices(field: FormField, path: FormPath, root: FormValue) : Promise<Choice[]>;
    /** The JSON Schema of a sub-form the driver gives at run time. */
    form(path: FormPath, root: FormValue) : Promise<Record<string, unknown>>;
    /** What the source gives as the whole form's value stands; left out where nothing can tell. */
    describe?(root: FormValue) : Promise<SourceDescription>;
}

/**
 * Whether a choice fits a slot (T-10): what it gives, or what it leads to (`offers`), is among what
 * the slot needs. A choice the driver says nothing about fits: it cannot be judged.
 */
export function fitsSlot(choice: Choice, needs: SlotNeeds | undefined) : boolean {
    if (!needs || needs.gives.length === 0) {
        return true;
    }
    if (!choice.gives && !choice.offers) {
        return true;
    }

    return (choice.gives !== undefined && needs.gives.includes(choice.gives.kind)) || (choice.offers ?? []).some((kind) => needs.gives.includes(kind));
}

/**
 * The one choice to choose for the user (T-13): the only one that fits the slot, by what it gives;
 * none where another fits too, or the one that fits only leads further. Choices that say nothing
 * of what they give (an attribute with no value now) do not count: they cannot be said to fit.
 */
export function onlyFitting(choices: readonly Choice[], needs: SlotNeeds | undefined) : Choice | undefined {
    if (!needs || needs.gives.length === 0) {
        return undefined;
    }

    const fitting = choices.filter((choice) => (choice.gives !== undefined || choice.offers !== undefined) && fitsSlot(choice, needs));
    const [only] = fitting;

    return fitting.length === 1 && only?.gives !== undefined && needs.gives.includes(only.gives.kind) ? only : undefined;
}

/** Every choice, and when some do not fit the slot, those that do first, in sections of their own. */
export function choiceOptions(choices: readonly Choice[], needs: SlotNeeds | undefined) : { value: string; label: string; detail?: string; section?: string }[] {
    const fitting = choices.filter((choice) => fitsSlot(choice, needs));
    if (fitting.length === choices.length || fitting.length === 0) {
        return [...choices];
    }

    return [
        ...fitting.map((choice) => ({ ...choice, section: 'Fits this tile' })),
        ...choices.filter((choice) => !fitsSlot(choice, needs)).map((choice) => ({ ...choice, section: 'Other' })),
    ];
}

function isRecord(value: unknown) : value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A typed value from what the user typed or picked: a number for a number field. */
function typedValue(field: FormField, text: string | undefined) : unknown {
    if (text === undefined) {
        return undefined;
    }
    if ((field.kind === 'text' || field.kind === 'choice') && field.valueType === 'number') {
        const number = Number(text);

        return Number.isFinite(number) ? number : undefined;
    }

    return text;
}

/**
 * Whether a choice offers "None": an optional one without a default. With a default, "None" would
 * only mean the default again, under another name.
 */
const canClear = (field: FormField) : boolean => !field.isRequired && field.defaultValue === undefined;

const shown = (value: unknown) : string | undefined => (typeof value === 'string' || typeof value === 'number' ? String(value) : undefined);

/** What a renderer of a field of its own gets (a tile's slot, an icon): the field, its value, and the object it is in. */
export interface FieldRenderProps {
    readonly field: FormField;
    readonly value: unknown;
    readonly onChange: (value: unknown) => void;
    readonly path: FormPath;
    /** The object the field is in, and a way to change it all at once (a command and its argument). */
    readonly scope: FormValue;
    readonly setScope: (value: Record<string, unknown>) => void;
    /** The form the field is in, for a renderer that shows some of its other fields its own way (a gauge's colours). */
    readonly form: Form;
}

/** Draws a field its own way, or returns undefined for the form's way (null: nothing). */
export type FieldRenderer = (props: FieldRenderProps) => ReactNode | undefined;

interface Context {
    readonly ask: FormAsk | undefined;
    readonly needs: SlotNeeds | undefined;
    /** The whole form's value, for the driver's questions. */
    readonly root: FormValue;
    readonly renderField: FieldRenderer | undefined;
    readonly describeField: FieldDescriber | undefined;
}

/** Words a field its own way in a sub-form's summary (a tile's slot: what it reads), or returns undefined. */
export type FieldDescriber = (field: FormField, value: unknown) => string | undefined;

export interface FormViewProps {
    form: Form;
    value: FormValue;
    onChange: (value: Record<string, unknown>) => void;
    ask?: FormAsk | undefined;
    /** What the tile's slot needs: what fits comes first (T-10). */
    needs?: SlotNeeds | undefined;
    path?: FormPath;
    /** The whole form's value; this value when left out (the top form). */
    root?: FormValue;
    /** The values of the forms around this one, innermost last (for `../` dependencies). */
    parents?: readonly FormValue[];
    /** Draws fields of its own (spec §5.7: a tile's slots, icons, colours), at any depth. */
    renderField?: FieldRenderer | undefined;
    /** Words fields of its own in the summaries of sub-forms and list items. */
    describeField?: FieldDescriber | undefined;
    /** The way taken at first, where the value cannot tell (a table cell that will read, but does not yet). */
    initialWay?: number | undefined;
}

/** The form's rows: the way first if it has alternatives, then the fields in play. */
export function FormView(props: FormViewProps) : ReactNode {
    const { form, value, onChange, ask, needs, path = [], parents = [], renderField, describeField, initialWay } = props;
    const root = props.root ?? value;
    const [way, setWay] = useState<number | undefined>(initialWay);
    // As the form was opened: a field whose dependencies differ from these was just chosen for (T-13).
    const [opened] = useState(value);
    const active = activeAlternative(form, value, way);
    const fields = fieldsInPlay(form, value, way);
    const context: Context = { ask, needs, root, renderField, describeField };

    const rows: ReactNode[] = [];
    const alternatives = form.alternatives ?? [];
    if (alternatives.length > 1) {
        rows.push(
            <ChoiceField
                key="#way"
                label="Way"
                options={alternatives.map((alternative, index) => ({ value: String(index), label: alternative.title, detail: alternative.description }))}
                value={active === undefined ? undefined : String(active)}
                onChange={(chosen) => {
                    const index = Number(chosen);
                    setWay(index);
                    onChange(chooseAlternative(form, value, index));
                }}
                helper="One of these ways, each with its own fields."
            />,
        );
    }

    for (const field of fields) {
        const state = fieldState(field, fields, value, parents);
        if (state.status === 'hidden') {
            continue;
        }
        if (state.status === 'waiting') {
            rows.push(<WaitingRow key={field.name} label={field.title} waitingFor={state.waitingFor} />);
            continue;
        }

        const fieldPath = [...path, field.name];
        const change = (fieldValue: unknown) : void => onChange(setField(form, value, field.name, fieldValue, way));
        const own = renderField?.({ field, value: value[field.name], onChange: change, path: fieldPath, scope: value, setScope: onChange, form });
        if (own !== undefined) {
            rows.push(<Fragment key={field.name}>{own}</Fragment>);
            continue;
        }

        const waitsOn = JSON.stringify(dependencyValues(field, value, parents));
        rows.push(
            <FieldRow
                key={field.name}
                field={field}
                fieldValue={value[field.name]}
                waitsOn={waitsOn}
                isNewlyWaitedOn={waitsOn !== JSON.stringify(dependencyValues(field, opened, parents))}
                isAsOpened={waitsOn === JSON.stringify(dependencyValues(field, opened, parents)) && value[field.name] === opened[field.name]}
                path={fieldPath}
                parents={[...parents, value]}
                context={context}
                onChange={change}
            />,
        );
    }

    return <>{rows}</>;
}

interface FieldRowProps {
    field: FormField;
    fieldValue: unknown;
    /** The values the field waits on, as a key: its choices are asked again when they change. */
    waitsOn: string;
    /** Whether what the field waits on was chosen since the form opened (T-13). */
    isNewlyWaitedOn?: boolean;
    /** Whether the field and what it waits on are as they were when the form opened. */
    isAsOpened?: boolean;
    path: FormPath;
    /** The values of the forms around a sub-form of this field, innermost last. */
    parents: readonly FormValue[];
    context: Context;
    onChange: (value: unknown) => void;
}

function FieldRow(props: FieldRowProps) : ReactNode {
    const { field, fieldValue, path, onChange } = props;

    switch (field.kind) {
        case 'choice':
            if (field.isListed) {
                return <ListedChoice {...props} />;
            }

            return (
                <ChoiceField
                    label={field.title}
                    // Values may carry names (`x-labels`): "Next hours" for `short`.
                    options={(field.values ?? []).map((value) => ({ value, label: choiceLabels(field)[value] ?? value }))}
                    value={shown(fieldValue ?? field.defaultValue)}
                    onChange={(chosen) => onChange(typedValue(field, chosen))}
                    isOptional={canClear(field)}
                    noneLabel={noneLabelOf(field)}
                    placeholder={field.isRequired ? 'Choose…' : noneLabelOf(field)}
                    helper={field.description}
                />
            );
        case 'text':
            return (
                <TextFieldRow
                    key={`${path.join('/')}:${shown(fieldValue) ?? ''}`}
                    label={field.title}
                    detail={field.description}
                    value={shown(fieldValue)}
                    type={field.valueType === 'number' ? 'number' : 'text'}
                    placeholder={field.isRequired ? 'Required' : 'None'}
                    onChange={(typed) => onChange(typedValue(field, typed))}
                />
            );
        case 'toggle':
            return <ToggleRow label={field.title} detail={field.description} checked={fieldValue === true} onChange={(checked) => onChange(checked)} />;
        case 'group':
            return <GroupRow {...props} form={field.form} />;
        case 'list':
            return <ListRow {...props} item={field.item} itemTitle={field.itemTitle} />;
        case 'driverForm':
            return <DriverFormRow {...props} />;
        case 'fixed':
            return null;
    }
}

/**
 * What leaving a field out means, where the schema says (`x-none`: "The whole device" for a device's
 * value, which reads all of it); else "None".
 */
const noneLabelOf = (field: FormField) : string => (typeof field.schema['x-none'] === 'string' ? field.schema['x-none'] : 'None');

/** A field the driver lists: its choices asked when the row is drawn, and again when what it waits on changes. */
function ListedChoice({ field, fieldValue, waitsOn, isNewlyWaitedOn = false, isAsOpened = true, path, context, onChange }: FieldRowProps) : ReactNode {
    const { ask, needs, root } = context;
    const [asked, retry] = useAsk<Choice[]>(ask ? `${path.join('/')}\u0000${waitsOn}` : undefined, () => (ask ? ask.choices(field, path, root) : Promise.resolve([])));
    // Counts the values cleared below, so that the one that fits is chosen after that too.
    const [clearedCount, setClearedCount] = useState(0);

    // A value kept when what it waits on was changed (`setField`, F-4) goes when the new choices come
    // without it. Not while it and what it waits on are as opened: a value that is just not there now
    // (a device offline) stays. Back on the device it opened with, another device's value goes too.
    useEffect(() => {
        if (asked?.status !== 'done' || isAsOpened || fieldValue === undefined) {
            return;
        }
        if (!asked.value.some((choice) => choice.value === shown(fieldValue))) {
            onChange(undefined);
            setClearedCount((count) => count + 1);
        }
        // `asked` holds the choices for `waitsOn`; the rest is read as it is when they come.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asked]);

    // Whether the source as it stands, with this field left out, is something the tile cannot show (a
    // status tile on a light read whole, the Mac's Hue check of 2026-11-03): then the field says so,
    // where T-13 chose nothing because more than one value fits.
    const [isUnfit, setUnfit] = useState(false);
    useEffect(() => {
        if (fieldValue !== undefined || field.isRequired || !needs || needs.gives.length === 0 || !ask?.describe || asked?.status !== 'done') {
            setUnfit(false);

            return undefined;
        }

        let isCurrent = true;
        ask.describe(root).then(
            ({ gives }) => {
                if (isCurrent) {
                    setUnfit(gives !== undefined && !needs.gives.includes(gives.kind));
                }
            },
            () => undefined,
        );

        return () => {
            isCurrent = false;
        };
        // What the source gives changes with the choices (`asked`) and this field's value.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asked, fieldValue]);

    // The one value that fits, chosen for the user when the source as it stands does not fit (T-13):
    // once per answer to what the field waits on, so that a value the user puts back stays.
    useEffect(() => {
        const only = asked?.status === 'done' ? onlyFitting(asked.value, needs) : undefined;
        if (!only || !needs || !ask?.describe || !isNewlyWaitedOn || field.isRequired || fieldValue !== undefined) {
            return undefined;
        }

        let isCurrent = true;
        ask.describe(root).then(
            ({ gives }) => {
                // What cannot be judged fits, as a choice does (`fitsSlot`).
                if (isCurrent && gives !== undefined && !needs.gives.includes(gives.kind)) {
                    onChange(typedValue(field, only.value));
                }
            },
            () => {
                // What it gives as it stands is not known: the user chooses.
            },
        );

        return () => {
            isCurrent = false;
        };
        // `asked` holds the choices for `waitsOn`; the rest is read as it is when they come.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asked, clearedCount]);

    return (
        <ChoiceField
            label={field.title}
            options={asked?.status === 'done' ? choiceOptions(asked.value, needs) : []}
            value={shown(fieldValue ?? field.defaultValue)}
            onChange={(chosen) => onChange(typedValue(field, chosen))}
            isOptional={canClear(field)}
            noneLabel={noneLabelOf(field)}
            placeholder={field.isRequired ? 'Choose…' : noneLabelOf(field)}
            helper={field.description}
            waiting={ask && (!asked || asked.status === 'waiting') ? 'Asking the driver…' : undefined}
            failed={!ask ? { reason: 'nothing to ask for its values', onRetry: retry } : asked?.status === 'failed' ? { reason: asked.reason, onRetry: retry } : undefined}
            filterFrom={FILTER_FROM}
            warning={isUnfit ? `${noneLabelOf(field)}: this tile can't show it. Choose one` : undefined}
        />
    );
}

/** What a field is for, at the top of its screen. */
function About({ text }: { text: string | undefined }) : ReactNode {
    return text ? (
        <Text fontSize="sm" color="fg.muted" px="1">
            {text}
        </Text>
    ) : null;
}

/** A sub-form (F-1): a row with what is set inside, opening a screen with its fields. */
function GroupRow({ field, fieldValue, path, parents, context, onChange, form }: FieldRowProps & { form: Form }) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const value = isRecord(fieldValue) ? fieldValue : {};

    return (
        <ScreenRow
            label={field.title}
            value={summarize(form, value, context.describeField) || 'Not set'}
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <Stack gap="4">
                    <About text={field.description} />
                    <SettingsGroup>
                        <FormView
                            form={form}
                            value={value}
                            onChange={(next) => onChange(Object.keys(next).length > 0 ? next : undefined)}
                            ask={context.ask}
                            needs={context.needs}
                            path={path}
                            root={context.root}
                            parents={parents}
                            renderField={context.renderField} describeField={context.describeField}
                        />
                    </SettingsGroup>
                    {/* An optional group is left out as a whole (F-1): its required fields could not be emptied one by one. */}
                    {!field.isRequired && fieldValue !== undefined ? (
                        <SettingsGroup>
                            <SettingsRow
                                icon="trash"
                                title="Remove"
                                color="#f87171"
                                opens={false}
                                onClick={() => {
                                    setOpen(false);
                                    onChange(undefined);
                                }}
                            />
                        </SettingsGroup>
                    ) : null}
                </Stack>
            }
        />
    );
}

/** A list of sub-forms (F-2): a row with how many, opening a screen with the items, "Add", and each item's screen. */
function ListRow({ field, fieldValue, path, parents, context, onChange, item, itemTitle }: FieldRowProps & { item: Form; itemTitle: string }) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const items = Array.isArray(fieldValue) ? fieldValue.map((each) => (isRecord(each) ? each : {})) : [];
    const write = (next: readonly Record<string, unknown>[]) : void => onChange(next.length > 0 ? next : undefined);

    return (
        <ScreenRow
            label={field.title}
            value={items.length === 0 ? 'None' : `${items.length}`}
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <Stack gap="4">
                    <About text={field.description} />
                    <SettingsGroup>
                        {items.map((each, index) => (
                            <ItemRow
                                key={index}
                                title={`${itemTitle} ${index + 1}`}
                                form={item}
                                value={each}
                                path={[...path, index]}
                                parents={parents}
                                context={context}
                                isFirst={index === 0}
                                isLast={index === items.length - 1}
                                onChange={(next) => write(items.map((other, at) => (at === index ? next : other)))}
                                onMove={(by) => {
                                    const moved = [...items];
                                    const [taken] = moved.splice(index, 1);
                                    moved.splice(index + by, 0, taken!);
                                    write(moved);
                                }}
                                onRemove={() => write(items.filter((_, at) => at !== index))}
                            />
                        ))}
                        <SettingsRow icon="plus" title={`Add ${itemTitle.toLowerCase()}`} color="#60a5fa" opens={false} onClick={() => write([...items, {}])} />
                    </SettingsGroup>
                </Stack>
            }
        />
    );
}

interface ItemRowProps {
    title: string;
    form: Form;
    value: Record<string, unknown>;
    path: FormPath;
    parents: readonly FormValue[];
    context: Context;
    isFirst: boolean;
    isLast: boolean;
    onChange: (value: Record<string, unknown>) => void;
    onMove: (by: -1 | 1) => void;
    onRemove: () => void;
}

/** One item of a list: a row opening its screen, with its fields, and moving and removing it. */
function ItemRow({ title, form, value, path, parents, context, isFirst, isLast, onChange, onMove, onRemove }: ItemRowProps) : ReactNode {
    const [isOpen, setOpen] = useState(false);

    return (
        <ScreenRow
            label={title}
            value={summarize(form, value, context.describeField) || 'Not set'}
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <Stack gap="4">
                    <SettingsGroup>
                        <FormView form={form} value={value} onChange={onChange} ask={context.ask} needs={context.needs} path={path} root={context.root} parents={parents} renderField={context.renderField} describeField={context.describeField} />
                    </SettingsGroup>
                    <SettingsGroup>
                        {isFirst ? null : <SettingsRow icon="arrow-up" title="Move up" opens={false} onClick={() => onMove(-1)} />}
                        {isLast ? null : <SettingsRow icon="arrow-down" title="Move down" opens={false} onClick={() => onMove(1)} />}
                        <SettingsRow
                            icon="trash"
                            title="Remove"
                            color="#f87171"
                            opens={false}
                            onClick={() => {
                                setOpen(false);
                                onRemove();
                            }}
                        />
                    </SettingsGroup>
                </Stack>
            }
        />
    );
}

/** A sub-form the driver gives at run time (F-5): asked when its screen opens, and again when what it waits on changes. */
function DriverFormRow({ field, fieldValue, waitsOn, path, parents, context, onChange }: FieldRowProps) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const value = isRecord(fieldValue) ? fieldValue : {};

    return (
        <ScreenRow
            label={field.title}
            value={Object.keys(value).length > 0 ? `${Object.keys(value).length} set` : 'Not set'}
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <Stack gap="4">
                    <About text={field.description} />
                    <DriverForm path={path} waitsOn={waitsOn} value={value} parents={parents} context={context} onChange={(next) => onChange(Object.keys(next).length > 0 ? next : undefined)} />
                </Stack>
            }
        />
    );
}

function DriverForm({ path, waitsOn, value, parents, context, onChange }: { path: FormPath; waitsOn: string; value: Record<string, unknown>; parents: readonly FormValue[]; context: Context; onChange: (value: Record<string, unknown>) => void }) : ReactNode {
    const { ask, root } = context;
    const [asked, retry] = useAsk<Record<string, unknown>>(ask ? `${path.join('/')}\u0000${waitsOn}` : undefined, () => (ask ? ask.form(path, root) : Promise.resolve({})));
    const form = useMemo(() => (asked?.status === 'done' ? readForm(asked.value) : undefined), [asked]);

    if (!asked || asked.status === 'waiting') {
        return <Waiting text="Asking the driver…" />;
    }
    if (asked.status === 'failed') {
        return <Failed reason={asked.reason} onRetry={retry} />;
    }

    return (
        <SettingsGroup>
            <FormView form={form ?? { fields: [] }} value={value} onChange={onChange} ask={ask} needs={context.needs} path={path} root={root} parents={parents} renderField={context.renderField} describeField={context.describeField} />
        </SettingsGroup>
    );
}
